import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import DashboardCards from '../components/dashboard/DashboardCards';
import Card, { CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { UserService, VSLAService, MembershipService, TransactionService } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';

interface DashboardStats {
  totalSavings: number;
  totalLoans: number;
  activeMemberships: number;
  completedMeetings: number;
}

interface RecentTransaction {
  id: string;
  type: string;
  amount: number;
  description: string;
  date: string;
  status: string;
}

const Dashboard: React.FC = () => {
  const { userData } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalSavings: 0,
    totalLoans: 0,
    activeMemberships: 0,
    completedMeetings: 0,
  });
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userData) {
      fetchDashboardData();
    }
  }, [userData]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user's memberships
      const membershipsResponse = await MembershipService.getMemberships({
        userId: userData!.id,
        status: 'active',
      });

      if (membershipsResponse.success && membershipsResponse.data) {
        const memberships = membershipsResponse.data;
        
        // Calculate stats from memberships
        const totalSavings = memberships.reduce((sum, m) => sum + m.totalSavings, 0);
        const totalLoans = memberships.reduce((sum, m) => sum + m.totalLoans, 0);
        const activeMemberships = memberships.length;

        // Fetch recent transactions
        const transactionsResponse = await TransactionService.getUserTransactions(userData!.id);
        const transactions = transactionsResponse.success && transactionsResponse.data 
          ? transactionsResponse.data.slice(0, 5).map(t => ({
              id: t.id,
              type: t.type,
              amount: t.amount,
              description: t.description,
              date: t.createdAt,
              status: t.status,
            }))
          : [];

        setStats({
          totalSavings,
          totalLoans,
          activeMemberships,
          completedMeetings: 0, // This would need to be calculated from meetings
        });
        setRecentTransactions(transactions);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'contribution':
        return '💰';
      case 'loan_disbursement':
        return '🏦';
      case 'loan_repayment':
        return '💳';
      case 'social_fund':
        return '🤝';
      case 'penalty':
        return '⚠️';
      default:
        return '📋';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          </div>
          <DashboardCards stats={stats} loading={true} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {userData?.firstName}!
            </h1>
            <p className="text-gray-600 mt-1">
              Here's what's happening with your VSLA activities
            </p>
          </div>
        </div>

        {error && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 text-red-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        <DashboardCards stats={stats} loading={loading} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {recentTransactions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No recent transactions found
                </p>
              ) : (
                <div className="space-y-4">
                  {recentTransactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {getTransactionIcon(transaction.type)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(transaction.date)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(transaction.amount)}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="/contributions"
                  className="flex flex-col items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <span className="text-2xl mb-2">💰</span>
                  <span className="text-sm font-medium text-blue-900">
                    Make Contribution
                  </span>
                </a>
                
                <a
                  href="/loans"
                  className="flex flex-col items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <span className="text-2xl mb-2">🏦</span>
                  <span className="text-sm font-medium text-green-900">
                    Apply for Loan
                  </span>
                </a>
                
                <a
                  href="/meetings"
                  className="flex flex-col items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <span className="text-2xl mb-2">📅</span>
                  <span className="text-sm font-medium text-purple-900">
                    View Meetings
                  </span>
                </a>
                
                <a
                  href="/reports"
                  className="flex flex-col items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <span className="text-2xl mb-2">📋</span>
                  <span className="text-sm font-medium text-orange-900">
                    View Reports
                  </span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Meetings */}
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Meetings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>No upcoming meetings scheduled</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
